import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps } from '@/types/chatbot'

const ctaIcons: Record<string, typeof Phone> = {
  phone: Phone,
  email: Mail,
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isUser
            ? 'bg-brand-primary text-white rounded-br-sm'
            : 'bg-white text-brand-dark rounded-bl-sm shadow-sm'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span
              className="w-2 h-2 bg-brand-secondary rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-brand-secondary rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-brand-secondary rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        )}
      </div>

      {/* Contact CTAs - only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 ml-1">
          {contactCTAs.map((cta) => {
            const Icon = ctaIcons[cta.id]
            return (
              <a
                key={cta.id}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[10px] sm:text-xs text-brand-secondary hover:text-brand-primary transition-colors"
              >
                {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                <span>{t(cta.labelKey)}</span>
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
